import React, { useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import EmailIllustrationSrc from "images/keyservice/keyhousecontact.avif";
import axios from "axios";
import { BASE_URL } from "../../index";
import { Alert } from "flowbite-react";

const Container = tw.div`relative`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-20 md:py-24`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0 flex justify-center items-center h-80 md:h-auto`;
const TextColumn = styled(Column)(props => [
  tw`md:w-7/12 mt-16 md:mt-0`,
  props.textOnLeft
    ? tw`md:mr-12 lg:mr-16 md:order-first`
    : tw`md:ml-12 lg:ml-16 md:order-last`,
]);

// ✅ Rounded-full image
const Image = styled.div(props => [
  `background-image: url("${props.imageSrc}");`,
  tw`w-64 h-64 md:w-80 md:h-80 rounded-full bg-cover bg-center shadow-lg`,
]);

const TextContent = tw.div`lg:py-8 text-center md:text-left`;
const Subheading = tw(SubheadingBase)`text-center md:text-left text-[#3F62A3]`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-left text-primary-500 text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`;

const Form = tw.form`mt-8 md:mt-10 text-sm flex flex-col max-w-sm mx-auto md:mx-0`;
const Input = tw.input`mt-6 first:mt-0 border-b-2 py-3 focus:outline-none font-medium transition duration-300 hocus:border-primary-500`;
const Textarea = styled(Input).attrs({ as: "textarea" })`${tw`h-24`}`;
const SubmitButton = tw(PrimaryButtonBase)`inline-block rounded-full mt-8`;

const LoadingBar = ({ progress }) => {
  const barStyles = {
    width: `${progress}%`,
    height: "7px",
    backgroundColor: "#00719c",
    transition: "width 0.3s ease-in-out",
    top: 0,
    left: 0,
    position: "fixed",
    zIndex: 9999,
  };
  return <div style={barStyles} />;
};

export default ({
  subheading = "Contacteer Ons",
  heading = (
    <>
      Voel je vrij om <span tw="text-[#00719c]">in contact te komen</span>
      <wbr /> met ons.
    </>
  ),
  description = "Heb je verder nog vragen of is iets niet duidelijk, kan je ons altijd bellen of hieronder een bericht achterlaten.",
  submitButtonText = "Verstuur",
  formAction = `${BASE_URL}/send-email`,
  formMethod = "POST",
  textOnLeft = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertColor, setAlertColor] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let currentProgress = 0;
      const interval = setInterval(() => {
        currentProgress += 15;
        setProgress(currentProgress);
        if (currentProgress >= 100) clearInterval(interval);
      }, 500);

      const response = await axios.post(formAction, formData, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 200) {
        setAlertColor("success");
        setAlertTitle("Success!");
        setAlertMessage("Email is verstuurd!");
        setShowAlert(true);
        setFormData({
          email: "",
          name: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        setAlertColor("failure");
        setAlertTitle("Fout!");
        setAlertMessage(
          "Er is iets fout gegaan tijdens het versturen, probeer het later opnieuw."
        );
        setShowAlert(true);
      }
    } catch {
      setAlertColor("warning");
      setAlertTitle("Waarschuwing!");
      setAlertMessage("Vul alle benodigde velden in.");
      setShowAlert(true);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <>
      <div id="contact" className="w-full max-w-sm my-10 relative z-[1000]">
        <div className="fixed m-3 top-0 left-0 right-0">
          {showAlert && (
            <Alert
              color={alertColor}
              withBorderAccent
              onDismiss={() => setShowAlert(false)}
            >
              <span className="font-medium">{alertTitle} </span>
              {alertMessage}
            </Alert>
          )}
        </div>
      </div>

      <Container id="contact">
        {loading && <LoadingBar progress={progress} />}
        <TwoColumn>
          <ImageColumn>
            <Image imageSrc={EmailIllustrationSrc} />
          </ImageColumn>
          <TextColumn textOnLeft={textOnLeft}>
            <TextContent>
              {subheading && <Subheading>{subheading}</Subheading>}
              <Heading>{heading}</Heading>
              {description && <Description>{description}</Description>}
              <Form action={formAction} method={formMethod} onSubmit={handleSubmit}>
                <Input
                  type="email"
                  name="email"
                  required
                  placeholder="Je email-adres"
                  value={formData.email}
                  onChange={handleInputChange}
                />
                <Input
                  type="text"
                  name="name"
                  required
                  placeholder="Volledige naam"
                  value={formData.name}
                  onChange={handleInputChange}
                />
                <Input
                  type="number"
                  name="phone"
                  required
                  placeholder="Je telefoonnummer"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
                <Input
                  type="text"
                  name="subject"
                  required
                  placeholder="Onderwerp"
                  value={formData.subject}
                  onChange={handleInputChange}
                />
                <Textarea
                  name="message"
                  placeholder="Je bericht..."
                  value={formData.message}
                  onChange={handleInputChange}
                />
                <SubmitButton tw="w-full" type="submit">
                  {submitButtonText}
                </SubmitButton>
              </Form>
            </TextContent>
          </TextColumn>
        </TwoColumn>
      </Container>
    </>
  );
};
